import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown, Icon, ResponsiveMode } from '@bingads-webui-react/fabric-control';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { getCustomDropdownStyle } from './style';

// The current dropdown we use will have an fault aria-activeDescendant prop which will lead to an A11y issue
// If the selected items is not an array
const dummyOptionKeys = ['foo', 'foo2'];
const dummyOptions = [
  { key: 'foo', text: null },
  { key: 'foo2', text: null },
];

// eslint-disable-next-line react/prop-types
const CustomDropdownList = ({ setOpen, onKeyDown, children }) => {
  //
  useEffect(() => {
    setOpen(true);
    return () => setOpen(false);
  }, [setOpen]);

  return (
    <div className="asset-library-custom-drop-down-list" tabIndex="-1" role="listbox" onKeyDown={onKeyDown}>
      {children}
    </div>
  );
};

// customized FluentUI Dropdown control, to replace the default
// dropdown list with provided content (children)
const CustomDropdownInternal = ({
  placeholder,
  title,
  children, // the content of dropdown list
  onClear, // callback when clicking on X button in the title
  onDismiss, // callback when the dropdonw list is dismissed
  dropdownWidth,
  focused, // focus the dropdown when it's mounted
  //
  classes,
}) => {
  //
  const [isDropdownOpen, setDropdownOpen] = useState(false);

  const hasTitle = useMemo(() => !!title, [title]);

  const clearValue = useCallback((evt) => {
    if (onClear) { onClear(isDropdownOpen); }
    if (evt && evt.stopPropagation) { evt.stopPropagation(); }
  }, [isDropdownOpen, onClear]);

  const onRenderCaretDown = useCallback((props, defaultOnRenderCaretDown) => {
    if (!hasTitle) {
      return defaultOnRenderCaretDown(props);
    }
    return (
      <Icon
        className="custom-dropdown-caretdown-selected"
        iconName="Cancel"
        aria-hidden
        onClick={clearValue}
      />
    );
  }, [hasTitle, clearValue]);

  const onRenderTitle = useCallback(
    () => title || placeholder || '',
    [placeholder, title]
  );

  const renderList = useCallback((props /* , defaultOnRenderList */) => {
    const { onDismiss: closeDropdown } = props || {};
    const onEnterPress = (event) => {
      if (event && event.key === 'Enter' && closeDropdown) {
        closeDropdown();
      }
    };
    return (
      <CustomDropdownList setOpen={setDropdownOpen} onKeyDown={onEnterPress}>
        {children}
      </CustomDropdownList>
    );
  }, [children]);

  const getDropdownStyles = useCallback((dropdownStyleProps) => {
    const { isOpen, theme } = dropdownStyleProps || {};
    const { palette } = theme || {};
    if (hasTitle) {
      return {
        title: {
          fontWeight: 600,
          backgroundColor: isOpen ? palette.themeLighterAlt : palette.white,
          borderColor: palette.themePrimary,
          color: isOpen ? palette.themeDark : palette.themePrimary,
        },
        dropdown: {
          '&:focus .ms-Dropdown-title, &:hover .ms-Dropdown-title, &:active .ms-Dropdown-title': {
            borderColor: palette.themePrimary,
            color: `${isOpen ? palette.themeDark : palette.themePrimary} !important`,
          },
        },
      };
    }
    return {
      title: {
        backgroundColor: isOpen ? palette.neutralLighter : palette.white,
      },
    };
  }, [hasTitle]);

  const onDropdownDismiss = useCallback(() => {
    if (onDismiss) {
      onDismiss();
    }
  }, [onDismiss]);

  const dropdownRef = useRef(null);
  useEffect(() => {
    if (focused) {
      dropdownRef.current.focus(true);
    }
  }, [focused]);

  return (
    <div className={classes.customDropdownContainer}>
      <Dropdown
        componentRef={dropdownRef}
        options={dummyOptions}
        selectedKeys={dummyOptionKeys}
        multiSelect
        onRenderCaretDown={onRenderCaretDown}
        onRenderList={renderList}
        onRenderTitle={onRenderTitle}
        // In case that other change on the dummyOptions and dummyOptionKeys might break the code and make the title not render
        // With the config now we use onRenderTitle to render title
        // https://github.com/microsoft/fluentui/blob/cbba19f7ed8a619b5fa856575fde872e79be99b9/packages/react/src/components/Dropdown/Dropdown.base.tsx#:~:text=the%20placeholder%20text-,selectedOptions.length,%3A%20onRenderPlaceholder(props%2C%20this._onRenderPlaceholder),-%7D
        onRenderPlaceholder={onRenderTitle}
        onDismiss={onDropdownDismiss}
        dropdownWidth={dropdownWidth}
        responsiveMode={ResponsiveMode.large}
        styles={getDropdownStyles}
        ariaLabel={placeholder}
      />
    </div>
  );
};

CustomDropdownInternal.propTypes = {
  placeholder: PropTypes.string.isRequired,
  title: PropTypes.string,
  children: PropTypes.node.isRequired,
  onClear: PropTypes.func,
  onDismiss: PropTypes.func,
  dropdownWidth: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  focused: PropTypes.bool,
  //
  classes: PropTypes.shape({
    customDropdownContainer: PropTypes.string,
  }).isRequired,
};

CustomDropdownInternal.defaultProps = {
  title: null,
  onClear: null,
  onDismiss: null,
  dropdownWidth: 0,
  focused: false,
};

export const CustomDropdown
  = withDefaultStyles(CustomDropdownInternal, getCustomDropdownStyle);
